import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';
import { ZoomMediaSelectedEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';
import { loadDeepZoom } from './artwork-deep-zoom.js?v=20261014-catalogue-1';

/**
 * A fitted artwork viewer. Zoom and pan stay inside the viewer; only Close exits.
 * @extends {Component<{dialog: HTMLDialogElement, media: HTMLElement[], thumbnails: HTMLElement}>}
 */
export class ZoomDialog extends Component {
  requiredRefs = ['dialog', 'media', 'thumbnails'];
  #highResRequests = new Set();
  #loadedWidths = new WeakMap();
  #index = 0;
  #scale = 1;
  #x = 0;
  #y = 0;
  #pointers = new Map();
  #controller;
  #closing = false;
  #opener;
  #gesture;
  #watchedImages = new WeakSet();
  #deepZoom;
  #deepZoomElement;
  #deepZoomImage;
  #deepZoomRequest = 0;
  #deepZoomLoading = false;

  connectedCallback() {
    super.connectedCallback();
    this.#controller = new AbortController();
    this.#watchedImages = new WeakSet();
    const options = { signal: this.#controller.signal };
    const stage = this.querySelector('.dialog-zoomed-gallery');
    stage.addEventListener('pointerdown', this.#pointerDown, options);
    stage.addEventListener('pointermove', this.#pointerMove, options);
    stage.addEventListener('pointerup', this.#pointerUp, options);
    stage.addEventListener('pointercancel', this.#pointerUp, options);
    stage.addEventListener('wheel', this.#wheel, { ...options, passive: false });
    stage.addEventListener('dragstart', (event) => event.preventDefault(), options);
    window.addEventListener('resize', () => {
      if (this.refs.dialog.open) {
        this.loadPreviewImage(this.refs.media[this.#index]);
        this.#setScale(this.#scale);
      }
      this.#render();
    }, options);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller?.abort();
    this.#destroyDeepZoom();
  }

  open(index, event) {
    event.preventDefault();
    if (this.#closing) return;
    this.#opener = event.target instanceof HTMLElement ? event.target.closest('button, [tabindex]') : null;
    this.refs.dialog.showModal();
    this.selectThumbnail(Number(index));
  }

  async close() {
    if (this.#closing || !this.refs.dialog.open) return;
    this.#closing = true;
    try {
      if (!prefersReducedMotion()) {
        await this.refs.dialog.animate([{ opacity: 1 }, { opacity: 0 }], {
          duration: 160,
          easing: 'ease-out',
        }).finished;
      }
    } finally {
      this.refs.dialog.close();
      this.#destroyDeepZoom();
      this.#pointers.clear();
      this.#closing = false;
      const opener = this.closest('media-gallery')?.querySelector(`[data-enlarge-index="${this.#index}"]`) || this.#opener;
      if (opener?.isConnected) opener.focus({ preventScroll: true });
      window.dispatchEvent(new DialogCloseEvent());
    }
  }

  handleKeyDown(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
    }
    if (event.target instanceof HTMLInputElement) return;
    if (event.key === '+' || event.key === '=') {
      event.preventDefault();
      this.zoomIn();
    }
    if (event.key === '-') {
      event.preventDefault();
      this.zoomOut();
    }
    if (event.key === '0') this.resetZoom();
    if (event.key === 'ArrowRight') this.selectThumbnail((this.#index + 1) % this.refs.media.length);
    if (event.key === 'ArrowLeft')
      this.selectThumbnail((this.#index + this.refs.media.length - 1) % this.refs.media.length);
  }

  handleThumbnailClick(index) {
    this.selectThumbnail(Number(index));
  }
  handleThumbnailPointerEnter(index) {
    if (this.refs.media[index]) this.loadPreviewImage(this.refs.media[index]);
  }

  selectThumbnail(index) {
    if (!this.refs.media[index]) return;
    this.#destroyDeepZoom();
    this.#index = index;
    const counter = this.querySelector('.artwork-viewer__counter');
    if (counter) counter.textContent = `${index + 1} / ${this.refs.media.length}`;
    this.#pointers.clear();
    this.refs.media.forEach((item, i) => {
      item.hidden = i !== index;
      item.classList.toggle('artwork-viewer__active', i === index);
    });
    this.refs.thumbnails.querySelectorAll('button').forEach((button, i) => {
      button.setAttribute('aria-selected', String(i === index));
    });
    this.querySelector('.artwork-viewer__controls').hidden = !this.refs.media[index].classList.contains(
      'product-media-container--image',
    );
    this.resetZoom();
    this.loadPreviewImage(this.refs.media[index]);
    for (const adjacent of [index - 1, index + 1]) {
      this.loadPreviewImage(this.refs.media[(adjacent + this.refs.media.length) % this.refs.media.length]);
    }
    this.#updateLoadingState();
    this.#startDeepZoom();
    this.dispatchEvent(new ZoomMediaSelectedEvent(index));
  }

  previousImage() {
    this.selectThumbnail((this.#index + this.refs.media.length - 1) % this.refs.media.length);
  }
  nextImage() {
    this.selectThumbnail((this.#index + 1) % this.refs.media.length);
  }

  zoomIn() {
    if (this.#deepZoom?.viewport) return this.#zoomDeepBy(1.4);
    if (this.#deepZoomLoading) return;
    this.#setScale(this.#scale * 1.3);
  }
  zoomOut() {
    if (this.#deepZoom?.viewport) return this.#zoomDeepBy(1 / 1.4);
    if (this.#deepZoomLoading) return;
    this.#setScale(this.#scale / 1.3);
  }
  resetZoom() {
    if (this.#deepZoom?.viewport) return this.#deepZoom.viewport.goHome();
    this.#x = 0;
    this.#y = 0;
    this.#setScale(1);
  }
  changeZoom(event) {
    if (this.#deepZoom?.viewport) {
      this.#deepZoom.viewport.zoomTo(Number(event.target.value));
      this.#deepZoom.viewport.applyConstraints();
      return;
    }
    if (this.#deepZoomLoading) return;
    this.#setScale(Number(event.target.value));
  }

  #setScale(value) {
    if (this.#deepZoom || this.#deepZoomLoading) return;
    const previous = this.#scale;
    this.#scale = Math.min(this.#maximumScale(), Math.max(1, value));
    if (this.#scale > 1) this.loadHighResolutionImage(this.refs.media[this.#index]);
    this.#x *= this.#scale / previous;
    this.#y *= this.#scale / previous;
    this.#render();
  }

  #maximumScale() {
    const image = this.refs.media[this.#index]?.querySelector('img.product-media__image');
    const width = Number(image?.getAttribute('width'));
    const height = Number(image?.getAttribute('height'));
    const stage = this.querySelector('.dialog-zoomed-gallery');
    if (!(width > 0 && height > 0 && stage.clientWidth > 0 && stage.clientHeight > 0)) return 1;
    const fittedWidth = Math.min(stage.clientWidth, stage.clientHeight * width / height);
    // Allow inspection at one source pixel per CSS pixel; DPR still controls download quality.
    const availableWidth = Math.min(Number(image.getAttribute('data_original_width')) || width, image.getAttribute('data_detail_srcset') ? Infinity : 5760);
    return Math.max(1, availableWidth / fittedWidth);
  }

  #render() {
    if (this.#deepZoom || this.#deepZoomLoading) return;
    const item = this.refs.media[this.#index];
    const image = item?.querySelector('img.product-media__image');
    if (!image) return;
    const stage = this.querySelector('.dialog-zoomed-gallery');
    const width = stage.clientWidth;
    const height = stage.clientHeight;
    const ratio = Number(image.getAttribute('width')) / Number(image.getAttribute('height')) || 1;
    const fittedWidth = Math.min(width, height * ratio);
    const fittedHeight = Math.min(height, width / ratio);
    const limitX = Math.max(0, (fittedWidth * this.#scale - width) / 2);
    const limitY = Math.max(0, (fittedHeight * this.#scale - height) / 2);
    this.#x = Math.max(-limitX, Math.min(limitX, this.#x));
    this.#y = Math.max(-limitY, Math.min(limitY, this.#y));
    // Paint at the zoomed size instead of magnifying a cached, fitted-size layer.
    Object.assign(image.style, {
      position: 'absolute',
      width: `${fittedWidth * this.#scale}px`,
      height: `${fittedHeight * this.#scale}px`,
      maxWidth: 'none',
      left: `${(width - fittedWidth * this.#scale) / 2 + this.#x}px`,
      top: `${(height - fittedHeight * this.#scale) / 2 + this.#y}px`,
      transform: 'none',
      willChange: 'auto',
      transition: 'none',
    });
    item.classList.toggle('artwork-viewer__pannable', this.#scale > 1);
    item.classList.toggle('artwork-viewer__dragging', this.#pointers.size > 0);
    const slider = this.querySelector('input[type="range"]');
    slider.min = '1';
    slider.step = '0.01';
    slider.max = String(this.#maximumScale());
    slider.value = String(this.#scale);
    slider.setAttribute('aria-valuetext', `${Math.round(this.#scale * 100)} percent`);
    this.querySelector('[aria-label="Zoom out"]').disabled = this.#scale <= 1;
    this.querySelector('[aria-label="Zoom in"]').disabled = this.#scale >= Number(slider.max);
  }

  #destroyDeepZoom() {
    this.#deepZoomRequest++;
    this.#deepZoomLoading = false;
    this.#deepZoom?.destroy();
    this.#deepZoom = undefined;
    this.#deepZoomElement?.remove();
    this.#deepZoomElement = undefined;
    if (this.#deepZoomImage) this.#deepZoomImage.style.opacity = '';
    this.#deepZoomImage = undefined;
  }

  async #startDeepZoom() {
    const image = this.refs.media[this.#index]?.querySelector('img.product-media__image');
    if (!image || (!image.getAttribute('data_dzi_tiles') && !image.getAttribute('data_detail_manifest')) || !this.refs.dialog?.open) return;
    const request = ++this.#deepZoomRequest;
    this.#deepZoomLoading = true;
    delete image.dataset.detailError;
    const fail = () => {
      if (request !== this.#deepZoomRequest) return;
      this.#destroyDeepZoom();
      image.dataset.detailError = 'true';
      this.resetZoom();
      this.#updateLoadingState();
    };
    try {
      const create = await loadDeepZoom(image);
      if (request !== this.#deepZoomRequest || !this.refs.dialog.open || this.#closing) return;
      if (!create) {
        this.#deepZoomLoading = false;
        this.#updateLoadingState();
        return;
      }
      const element = document.createElement('div');
      element.className = 'artwork-viewer__deep-zoom';
      element.setAttribute('aria-label', image.alt || 'Artwork detail');
      Object.assign(element.style, { width: '100%', height: '100%', touchAction: 'none' });
      image.parentElement.append(element);
      this.#deepZoomElement = element;
      this.#deepZoomImage = image;
      this.#deepZoom = create(element, image);
      const sync = () => this.#syncDeepZoomControls();
      this.#deepZoom.addHandler('open', sync);
      this.#deepZoom.addHandler('zoom', sync);
      this.#deepZoom.addHandler('resize', sync);
      this.#deepZoom.addHandler('open-failed', fail);
      this.#deepZoom.addHandler('tile-load-failed', fail);
      this.#deepZoom.addHandler('canvas-key', (event) => {
        // The gallery owns keyboard navigation; avoid handling each key twice.
        event.preventDefaultAction = true;
      });
      this.#deepZoom.addOnceHandler('tile-drawn', () => {
        image.style.opacity = '0';
        this.#deepZoomLoading = false;
        sync();
        this.#updateLoadingState();
      });
    } catch {
      fail();
    }
  }

  #syncDeepZoomControls() {
    const viewport = this.#deepZoom?.viewport;
    if (!viewport) return;
    const slider = this.querySelector('input[type="range"]');
    slider.min = String(viewport.getMinZoom());
    slider.max = String(viewport.getMaxZoom());
    slider.step = '0.001';
    slider.value = String(viewport.getZoom());
    slider.setAttribute('aria-valuetext', `${Math.round(viewport.getZoom() / viewport.getHomeZoom() * 100)} percent`);
    this.querySelector('[aria-label="Zoom out"]').disabled = Number(slider.value) <= Number(slider.min);
    this.querySelector('[aria-label="Zoom in"]').disabled = Number(slider.value) >= Number(slider.max);
  }

  #zoomDeepBy(factor) {
    this.#deepZoom.viewport.zoomBy(factor);
    this.#deepZoom.viewport.applyConstraints();
  }

  #pointerDown = (event) => {
    if (this.#deepZoom || this.#deepZoomLoading) return;
    if (event.button !== 0 || !event.target.closest('.artwork-viewer__image')) return;
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    this.#gesture = this.#pointers.size === 0 ? { x: event.clientX, y: event.clientY, scale: this.#scale } : null;
    this.#pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
    this.#render();
  };

  #pointerMove = (event) => {
    const previous = this.#pointers.get(event.pointerId);
    if (!previous) return;
    const other = [...this.#pointers.entries()].find(([id]) => id !== event.pointerId)?.[1];
    if (other) {
      const before = Math.hypot(previous.x - other.x, previous.y - other.y);
      const after = Math.hypot(event.clientX - other.x, event.clientY - other.y);
      if (before > 0) this.#setScale((this.#scale * after) / before);
    } else {
      this.#x += event.clientX - previous.x;
      this.#y += event.clientY - previous.y;
    }
    this.#pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
    this.#render();
  };

  #pointerUp = (event) => {
    if (this.#deepZoom || this.#deepZoomLoading) return;
    const gesture = this.#gesture;
    this.#gesture = null;
    this.#pointers.delete(event.pointerId);
    if (gesture && event.type !== 'pointercancel') {
      const dx = event.clientX - gesture.x;
      const dy = event.clientY - gesture.y;
      if (Math.hypot(dx, dy) < 8) {
        if (this.#scale > 1) this.resetZoom();
        else this.#setScale(2);
      } else if (event.pointerType === 'touch' && gesture.scale === 1 && Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 1.5) {
        if (dx < 0) this.nextImage();
        else this.previousImage();
      }
    }
    this.#render();
  };

  #wheel = (event) => {
    if (this.#deepZoom || this.#deepZoomLoading) return;
    if (!event.target.closest('.artwork-viewer__image')) return;
    event.preventDefault();
    this.#setScale(this.#scale * Math.exp(-event.deltaY * 0.002));
  };

  loadPreviewImage(mediaContainer) {
    const image = mediaContainer?.querySelector('img.product-media__image');
    if (!image || !(image instanceof HTMLImageElement)) return;
    if (!this.#watchedImages.has(image)) {
      this.#watchedImages.add(image);
      image.addEventListener('load', () => {
        delete image.dataset.viewerError;
        mediaContainer.style.backgroundImage = '';
        this.#updateLoadingState();
        this.#render();
      }, { signal: this.#controller?.signal });
      image.addEventListener('error', () => {
        image.dataset.viewerError = 'true';
        this.#updateLoadingState();
      }, { signal: this.#controller?.signal });
    }
    const mediaId = image.closest('[data-media-id]')?.getAttribute('data-media-id');
    if (mediaId && !image.naturalWidth) {
      const preview = [...(this.closest('media-gallery')?.querySelectorAll('[data-media-id] img') || [])].find(
        (candidate) => !candidate.closest('dialog') && candidate.closest('[data-media-id]').getAttribute('data-media-id') === mediaId && candidate.complete && candidate.naturalWidth,
      );
      if (preview) mediaContainer.style.backgroundImage = `url(${JSON.stringify(preview.currentSrc)})`;
    }
    const stage = this.querySelector('.dialog-zoomed-gallery');
    const ratio = Number(image.getAttribute('width')) / Number(image.getAttribute('height')) || 1;
    const width = stage.clientWidth || window.innerWidth;
    const height = stage.clientHeight || window.innerHeight;
    // Match the fitted artwork, rather than downloading a full-viewport-width portrait.
    image.sizes = `${Math.ceil(Math.min(width, height * ratio))}px`;
    image.loading = 'eager';
  }

  #updateLoadingState() {
    const image = this.refs.media[this.#index]?.querySelector('img.product-media__image');
    const status = this.querySelector('.artwork-viewer__status');
    if (!status) return;
    const detailFailed = image?.dataset.detailError === 'true';
    const failed = image?.dataset.viewerError === 'true' || detailFailed;
    const loading = image && !failed && (!image.complete || !image.naturalWidth);
    status.hidden = !failed && !loading;
    status.querySelector('span').textContent = detailFailed ? 'Detail could not load. Please retry.' : failed ? 'This image could not load.' : loading ? 'Loading image…' : '';
    status.querySelector('button').hidden = !failed;
    if (image) image.style.visibility = image.naturalWidth ? '' : 'hidden';
  }

  retryImage() {
    const image = this.refs.media[this.#index]?.querySelector('img.product-media__image');
    if (!image) return;
    if (image.dataset.detailError) {
      this.#destroyDeepZoom();
      this.#startDeepZoom();
      this.#updateLoadingState();
      return;
    }
    delete image.dataset.viewerError;
    image.src = image.currentSrc || image.src;
    this.#updateLoadingState();
  }

  loadHighResolutionImage(mediaContainer) {
    if (!mediaContainer.classList.contains('product-media-container--image')) return false;

    const image = mediaContainer.querySelector('img.product-media__image');
    if (!image || !(image instanceof HTMLImageElement)) return false;
    if (image === this.#deepZoomImage || this.#deepZoomLoading) return false;

    const sourceUrl = image.getAttribute('data_max_resolution');
    if (!sourceUrl) return false;
    let highResolutionUrl = sourceUrl;
    let requestedWidth = Infinity;
    const url = new URL(sourceUrl, location.href);
    const isShopify = url.pathname.includes('/cdn/shop/files/') || (url.hostname === 'cdn.shopify.com' && url.pathname.includes('/files/'));
    const isShopifyAsset = /\/cdn\/shop\/t\/\d+\/assets\/.*\.jpg$/.test(url.pathname);
    const detailSources = (image.getAttribute('data_detail_srcset') || '').split(',').map(candidate => {
      const [src, descriptor] = candidate.trim().split(/\s+/);
      return { src, width: Number.parseInt(descriptor, 10) };
    }).filter(candidate => candidate.src && candidate.width > 0).sort((a, b) => a.width - b.width);
    if (detailSources.length) {
      const stage = this.querySelector('.dialog-zoomed-gallery');
      const ratio = Number(image.getAttribute('width')) / Number(image.getAttribute('height'));
      const needed = Math.min(stage.clientWidth, stage.clientHeight * ratio) * this.#scale * (window.devicePixelRatio || 1);
      const detail = detailSources.find(candidate => candidate.width >= needed) || detailSources.at(-1);
      requestedWidth = detail.width;
      highResolutionUrl = new URL(detail.src, location.href).href;
    } else if (isShopify || isShopifyAsset) {
      const originalWidth = Number(image.getAttribute('width'));
      const originalHeight = Number(image.getAttribute('height'));
      const stage = this.querySelector('.dialog-zoomed-gallery');
      if (originalWidth > 0 && originalHeight > 0 && stage.clientWidth && stage.clientHeight) {
        const fittedWidth = Math.min(stage.clientWidth, stage.clientHeight * originalWidth / originalHeight);
        const needed = fittedWidth * this.#scale * (window.devicePixelRatio || 1);
        requestedWidth = Math.min(Number(image.getAttribute('data_original_width')) || originalWidth, [832, 1200, 1600, 1920, 2560, 3840, 5760].find(width => width >= needed) || 5760);
        if (isShopifyAsset) url.pathname = url.pathname.replace(/\.jpg$/, `_${requestedWidth}x.jpg`);
        else url.searchParams.set('width', String(requestedWidth));
        highResolutionUrl = url.href;
      }
    }
    const loaded = this.#loadedWidths.get(image);
    // src is only the fallback while srcset can still display a smaller image.
    if ((loaded?.source === sourceUrl && loaded.width >= requestedWidth) || this.#highResRequests.has(highResolutionUrl)) return false;

    // Reserve the URL immediately so hover and scroll cannot start duplicate loads.
    this.#highResRequests.add(highResolutionUrl);
    const newImage = new Image();
    newImage.onload = async () => {
      try {
        await newImage.decode();
        if (!image.isConnected || image.getAttribute('data_max_resolution') !== sourceUrl || (this.#loadedWidths.get(image)?.source === sourceUrl && this.#loadedWidths.get(image).width > requestedWidth)) {
          this.#highResRequests.delete(highResolutionUrl);
          return;
        }
        // Keep dimensions, focal point, refs and zoom state on the original node.
        image.removeAttribute('srcset');
        image.src = highResolutionUrl;
        this.#loadedWidths.set(image, { source: sourceUrl, width: requestedWidth });
        this.#highResRequests.delete(highResolutionUrl);
      } catch {
        this.#highResRequests.delete(highResolutionUrl);
      }
    };
    newImage.onerror = () => this.#highResRequests.delete(highResolutionUrl);
    newImage.src = highResolutionUrl;
  }
}

if (!customElements.get('zoom-dialog')) customElements.define('zoom-dialog', ZoomDialog);
